import React, { useEffect, useMemo, useState } from "react";
import { getIDScan, decideIDScan } from "../../api/admin.js";
import ModalBasic from "../../components/ModalBasic.jsx";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import { addToast, getDashboardActions, getIDScans } from "../../store/app/action.js";
import { useDispatch } from "react-redux";

const ScanModal = ({ id, close }) => {
  const [scan, setScan] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [dob, setDob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const dispatch = useDispatch();
  const [reason, setReason] = useState("");
  const [reuploadModalOpen, setReuploadModalOpen] = useState(false);
  const documents = useMemo(() => {
    return scan?.api_result?.data?.user_documents || [];
  }, [scan]);

  const underage = useMemo(() => {
    if (dob?.[0]) {
      let age = moment().diff(dob[0], "years");
      return age < 21;
    }
    return false;
  }, [dob]);

  useEffect(() => {
    setReason("");
  }, [reuploadModalOpen]);

  useEffect(() => {
    if (!id) {
      setScan(null);
    } else {
      getIDScan(id).then((res) => {
        if (res.scan) {
          setScan(res.scan);
        } else {
          setScan(null);
        }
      });
    }
  }, [id]);

  const defaultDate = useMemo(() => {
    if (
      scan?.api_result?.data?.confirmed_kyc_info?.dob &&
      scan?.api_result?.data?.confirmed_kyc_info?.dob !== "0001-01-01T00:00:00Z"
    ) {
      return moment(
        scan?.api_result?.data.confirmed_kyc_info?.dob.split("T")[0]
      ).toDate();
    } else {
      let documents = scan?.api_result?.data?.user_documents || [];
      for (let i = 0; i < documents.length; i++) {
        if (
          documents[i].kyc_info?.dob &&
          documents[i].kyc_info?.dob !== "0001-01-01T00:00:00Z"
        ) {
          return moment(documents[i].kyc_info?.dob.split("T")[0]).toDate();
        }
      }
    }
    return new Date();
  }, [scan]);
  useEffect(() => {
    setDob([defaultDate]);
  }, [defaultDate]);
  const decide = async (action) => {
    setLoading(true);
    try {
      await decideIDScan(id, { action, dob: dob[0], reason });
      await dispatch(getIDScans());
      await dispatch(getDashboardActions());
      setLoading(false);
      setReuploadModalOpen(false);
      close();
    } catch (e) {
      let message =
        "There was an error processing your request.  Please try again later.";
      let doClose = false;
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage === "Scan has already been reviewed") {
          message =
            "This scan has already been reviewed by another team member.";
          doClose = true;
        }
      } catch {}
      setModal({
        message,
        continueAction: () => {
          setModal(null);
          setLoading(false);
          if (doClose) {
            close();
          }
        },
      });
    }
  };
  return (
    <ModalBasic
      title={"ID Scan"}
      width="5xl"
      setModalOpen={close}
      modalOpen={!!id}
    >
      {scan ? (
        <>
          <div>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-xl">{`${scan.customer.first_name} ${scan.customer.last_name}`}</div>
              <div className="text-slate-500">{`Scan ID: ${scan.scan_id}`}</div>
            </div>
            <div className="text-slate-500">{`Acquisition Channel: ${
              scan.channel || "Marketplace"
            }`}</div>
            <div className="text-slate-500">{`Received: ${
              scan.api_result?.data?.date_submitted
                ? moment(scan.api_result?.data?.date_submitted)
                : "Unknown"
            }`}</div>
          </div>
          <div className="flex flex-1">
            <div className="flex flex-col flex-1 pt-8 gap-6 mr-8">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Birthday <span className="text-rose-500">*</span>
                </label>
                <Flatpickr
                  className={"form-input w-48"}
                  onChange={(date) => {
                    setDob(date);
                  }}
                  options={{
                    dateFormat: "M j, Y",
                    defaultDate: defaultDate,
                  }}
                />
                {underage && (
                  <div className="text-red-500 text-sm">
                    Underage, if ID is genuine, submit as genuine. System will
                    unblock user on their 21st birthday.
                  </div>
                )}
              </div>
              <PrimaryButton
                loading={loading}
                onClick={async () => {
                  await decide("APPROVE");
                }}
              >
                Genuine ID
              </PrimaryButton>
              <DangerButton
                loading={loading}
                onClick={async () => {
                  setModal({
                    message:
                      "Are you sure?  Rejecting this ID cause the customer unable to make a purchase until they contact Customer Support.",
                    closeAction: () => setModal(null),
                    continueAction: async () => {
                      await decide("REJECT");
                    },
                  });
                }}
              >
                Fake ID, Ban User
              </DangerButton>
              <SecondaryButton
                loading={loading}
                // onClick={async () => {
                //   setModal({
                //     message: (
                //
                //     ),
                //     closeAction: () => setModal(null),
                //     continueAction: async () => {
                //       await decide("REQUEST");
                //     },
                //   });
                // }}
                onClick={() => setReuploadModalOpen(true)}
              >
                Request new upload
              </SecondaryButton>
            </div>
            <ModalBasic
              width={"lg"}
              setModalOpen={setReuploadModalOpen}
              modalOpen={reuploadModalOpen}
              title={"Select reason"}
            >
              <div className="flex flex-col gap-4">
                <div>
                  Select a reason for requesting the user to re-upload their ID.
                  <select
                    value={reason}
                    id="reason"
                    className="form-select w-full"
                    onChange={(e) => setReason(e.currentTarget.value)}
                  >
                    <option value="">Select a reason</option>
                    <option value="expired">Expired</option>
                    <option value="not_id">
                      Not a Valid Government-Issued ID
                    </option>
                    <option value="blurry">Blurry / Poor Image Quality</option>
                    <option value="obstructed">
                      Covered / Obstructed Information
                    </option>
                    <option value="mismatch">
                      Info Does Not Match Account Details
                    </option>
                    <option value="other">Other reasons</option>
                  </select>
                </div>
                <div className="flex gap-4 justify-end">
                  <SecondaryButton onClick={() => setReuploadModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    loading={loading}
                    onClick={async () => {
                      if (!reason) {
                        dispatch(
                          addToast({
                            message: "Please select a reason",
                            type: "error",
                            timer: 5,
                          })
                        );
                        return;
                      }
                      await decide("REQUEST");
                    }}
                  >
                    OK
                  </PrimaryButton>
                </div>
              </div>
            </ModalBasic>
            <ModalBasic
              width={"lg"}
              title={"Notes"}
              setModalOpen={modal?.closeAction}
              hideCloseButton={!modal?.closeAction}
              modalOpen={!!modal}
            >
              <div className="flex flex-col gap-4">
                {modal?.message}
                <div className="flex gap-4 justify-end">
                  {modal?.closeAction && (
                    <SecondaryButton onClick={modal?.closeAction}>
                      Cancel
                    </SecondaryButton>
                  )}
                  <PrimaryButton onClick={modal?.continueAction}>
                    OK
                  </PrimaryButton>
                </div>
              </div>
            </ModalBasic>

            <div className="flex flex-1">
              <div className="flex flex-1 flex-col justify-start  h-[600px]">
                <div className="gap-2 border-b p-2">
                  {documents.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedTab(index)}
                      className={`px-4 py-2 ${
                        selectedTab === index
                          ? "border-b-2 border-blue-500 font-semibold"
                          : ""
                      }`}
                    >
                      {doc.side || doc.type}
                    </button>
                  ))}
                </div>
                <div className="p-4">
                  {documents.map(
                    (doc, index) =>
                      selectedTab === index && (
                        <img
                          key={index}
                          src={doc.signed_url}
                          alt={doc.type}
                          className="max-w-full max-h-[500px] cursor-pointer mx-auto"
                          onClick={() => setExpanded(doc.signed_url)}
                        />
                      )
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-slate-500">Loading...</div>
      )}
      <ModalBasic
        width="2xl"
        title={"Document"}
        setModalOpen={() => setExpanded(null)}
        modalOpen={!!expanded}
      >
        <img src={expanded} alt="Document" className="max-w-full mx-auto" />
      </ModalBasic>
    </ModalBasic>
  );
};

export default ScanModal;
